import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IconMapPin, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";

import { api, ApiError } from "../lib/api";

/**
 * TPH master · Estate Land Management tab.
 * Block → (optional) sub-block → TPH platform with GPS.
 * TPHs registered here feed the Mandor's Grade tab, the Driver's
 * pickup flow and SPB dispatch. Rule of thumb ≈ 1 platform per
 * 5-6 planted ha along the harvesting road.
 */

interface Block {
  id: string;
  code: string;
  category: string;
  hectaresPlanted: string | null;
}
interface SubBlock {
  id: string;
  blockId: string;
  code: string;
  areaHa: string | null;
}
interface Tph {
  id: string;
  blockId: string;
  subBlockId: string | null;
  code: string;
  name: string | null;
  gpsLatitude: string | null;
  gpsLongitude: string | null;
}

export function TphMasterTab() {
  const qc = useQueryClient();
  const [blockId, setBlockId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const blocksQ = useQuery({
    queryKey: ["blocks"],
    queryFn: () => api.list<Block>("/v1/blocks?limit=500"),
  });
  const subBlocksQ = useQuery({
    queryKey: ["block-sub-blocks"],
    queryFn: () => api.list<SubBlock>("/v1/block-sub-blocks"),
  });
  const tphsQ = useQuery({
    queryKey: ["tphs"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => api.list<Tph>("/v1/tphs"),
  });

  const plantedBlocks = useMemo(
    () => (blocksQ.data?.data ?? []).filter((b) => b.category === "PLANTED"),
    [blocksQ.data],
  );
  const block = plantedBlocks.find((b) => b.id === blockId);
  const blockSubs = (subBlocksQ.data?.data ?? []).filter(
    (s) => s.blockId === blockId,
  );
  const subById = useMemo(() => {
    const m = new Map<string, SubBlock>();
    for (const s of subBlocksQ.data?.data ?? []) m.set(s.id, s);
    return m;
  }, [subBlocksQ.data]);
  const blockTphs = (tphsQ.data?.data ?? [])
    .filter((t) => t.blockId === blockId)
    .sort((a, b) => a.code.localeCompare(b.code));

  // Form state
  const [fCode, setFCode] = useState("");
  const [fSubBlockId, setFSubBlockId] = useState("");
  const [fName, setFName] = useState("");
  const [fLat, setFLat] = useState("");
  const [fLng, setFLng] = useState("");

  const openAdd = () => {
    setEditingId(null);
    setFCode(`${block?.code ?? ""}-TPH-${blockTphs.length + 1}`);
    setFSubBlockId("");
    setFName("");
    setFLat("");
    setFLng("");
    setShowForm(true);
  };
  const openEdit = (t: Tph) => {
    setEditingId(t.id);
    setFCode(t.code);
    setFSubBlockId(t.subBlockId ?? "");
    setFName(t.name ?? "");
    setFLat(t.gpsLatitude ?? "");
    setFLng(t.gpsLongitude ?? "");
    setShowForm(true);
  };

  const save = async () => {
    setError(null);
    const body = {
      blockId,
      subBlockId: fSubBlockId || null,
      code: fCode,
      name: fName || undefined,
      gpsLatitude: fLat || undefined,
      gpsLongitude: fLng || undefined,
    };
    try {
      if (editingId) {
        await api.patch(`/v1/tphs/${editingId}`, body);
      } else {
        await api.post("/v1/tphs", body);
      }
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ["tphs"] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const remove = async (t: Tph) => {
    if (
      !confirm(
        `Delete TPH ${t.code}?\n\nExisting audits keep their reference; the platform stops appearing in the Mandor / Driver pickers.`,
      )
    )
      return;
    setError(null);
    try {
      await api.del(`/v1/tphs/${t.id}`);
      qc.invalidateQueries({ queryKey: ["tphs"] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const recommended = block?.hectaresPlanted
    ? Math.max(1, Math.round(Number(block.hectaresPlanted) / 5.5))
    : null;

  return (
    <div className="space-y-4 text-sm">
      <div>
        <div className="font-medium">TPH master</div>
        <p className="text-xs text-text-secondary">
          Block → sub-block (optional) → platform. TPHs registered here
          appear in the Mandor's Grade picker, the Driver's pickup flow and
          SPB dispatch. Rule of thumb ≈ 1 platform per 5–6 planted ha.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs">
          Block
          <select
            value={blockId}
            onChange={(e) => setBlockId(e.target.value)}
            className="rounded border border-border bg-surface-0 px-2 py-1.5"
          >
            <option value="">— select block —</option>
            {plantedBlocks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} · {b.hectaresPlanted ?? "?"} ha
              </option>
            ))}
          </select>
        </label>
        {block && recommended !== null && (
          <span
            className={`text-xs ${blockTphs.length < recommended ? "text-amber-700" : "text-text-secondary"}`}
          >
            {blockTphs.length} platform(s) · ~{recommended} recommended for{" "}
            {block.hectaresPlanted} ha
          </span>
        )}
      </div>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
          {error}
        </div>
      )}

      {blockId && (
        <section className="rounded border border-border">
          <div className="flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2">
            <div className="font-medium">TPHs ({blockTphs.length})</div>
            <button
              onClick={openAdd}
              className="flex items-center gap-1 rounded bg-text-primary px-3 py-1 text-xs text-white hover:opacity-90"
            >
              <IconPlus size={13} /> Add TPH
            </button>
          </div>
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-surface-1 text-text-secondary">
              <tr>
                <th className="p-2 text-left">Code</th>
                <th className="p-2 text-left">Sub-block</th>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-center">GPS</th>
                <th className="p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {blockTphs.map((t) => (
                <tr key={t.id} className="border-b border-border">
                  <td className="p-2 font-mono font-medium">{t.code}</td>
                  <td className="p-2">
                    {t.subBlockId ? (
                      <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-emerald-800">
                        {block?.code}-{subById.get(t.subBlockId)?.code ?? "?"}
                      </span>
                    ) : (
                      <span
                        className="text-text-secondary"
                        title="Not linked to a sub-block — assign one for sub-block-level yield attribution"
                      >
                        whole block
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-text-secondary">{t.name ?? "—"}</td>
                  <td className="p-2 text-center">
                    {t.gpsLatitude && t.gpsLongitude ? (
                      <IconMapPin
                        size={12}
                        className="mx-auto text-emerald-600"
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="p-2">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => openEdit(t)}
                        className="rounded bg-slate-800 px-2 py-1 text-white hover:bg-slate-900"
                        title={`Edit ${t.code}`}
                      >
                        <IconPencil size={11} />
                      </button>
                      <button
                        onClick={() => void remove(t)}
                        className="rounded bg-red-600 px-2 py-1 text-white hover:bg-red-700"
                        title={`Delete ${t.code}`}
                      >
                        <IconTrash size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!blockTphs.length && (
                <tr>
                  <td
                    colSpan={5}
                    className="p-4 text-center text-text-secondary"
                  >
                    No TPHs on this block yet. Click <strong>Add TPH</strong>.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      )}

      {showForm && blockId && (
        <section className="rounded border border-border bg-surface-2 p-4">
          <div className="mb-3 font-medium">
            {editingId ? "Edit TPH" : "Add TPH"} · block {block?.code}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-text-secondary">
                Code
              </span>
              <input
                value={fCode}
                onChange={(e) => setFCode(e.target.value)}
                className="rounded border border-border bg-surface-0 px-2 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-text-secondary">
                Sub-block (optional)
              </span>
              <select
                value={fSubBlockId}
                onChange={(e) => setFSubBlockId(e.target.value)}
                disabled={!blockSubs.length}
                className="rounded border border-border bg-surface-0 px-2 py-1.5 disabled:opacity-50"
              >
                <option value="">
                  {blockSubs.length
                    ? "— whole block —"
                    : "— no sub-blocks defined (Palms tab) —"}
                </option>
                {blockSubs.map((s) => (
                  <option key={s.id} value={s.id}>
                    {block?.code}-{s.code} · {s.areaHa ?? "?"} ha
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-text-secondary">
                Name (optional)
              </span>
              <input
                value={fName}
                onChange={(e) => setFName(e.target.value)}
                placeholder="Roadside platform near culvert"
                className="rounded border border-border bg-surface-0 px-2 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-text-secondary">
                GPS lat
              </span>
              <input
                value={fLat}
                onChange={(e) => setFLat(e.target.value)}
                placeholder="e.g. 5.421823"
                className="rounded border border-border bg-surface-0 px-2 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-text-secondary">
                GPS lng
              </span>
              <input
                value={fLng}
                onChange={(e) => setFLng(e.target.value)}
                placeholder="e.g. 117.938451"
                className="rounded border border-border bg-surface-0 px-2 py-1.5"
              />
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => setShowForm(false)}
              className="rounded border border-border bg-surface-0 px-3 py-1.5 text-xs hover:bg-surface-1"
            >
              Cancel
            </button>
            <button
              onClick={() => void save()}
              disabled={!fCode}
              className="rounded bg-text-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {editingId ? "Save changes" : "Add"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
